import React, { useMemo, useState } from 'react';
import { X, Cable as CableIcon, Trash2 } from 'lucide-react';
import { useProjectStore } from '../../store/projectStore';
import { PlacedEquipment } from '../../types';
import PortsView from './PortsView';
import { parsePortEndpointId } from '../../utils/portNamingSchemes';

interface CablePortEditorProps {
  isOpen: boolean;
  onClose: () => void;
}

const CablePortEditor: React.FC<CablePortEditorProps> = ({ isOpen, onClose }) => {
  const {
    currentProject,
    selectedCable,
    updateCableRun,
    deleteCableRun,
    getCurrentDesign,
    appTheme,
  } = useProjectStore();
  const currentDesign = getCurrentDesign();
  const isDark = appTheme === 'dark';
  const [confirmDelete, setConfirmDelete] = useState<{
    type: 'cable';
    id: string;
    label: string;
  } | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastFading, setToastFading] = useState(false);

  const drawerBg = isDark ? '#20242b' : '#ffffff';
  const border = isDark ? '#343a43' : '#e5e7eb';
  const text = isDark ? '#e5e7eb' : '#111827';
  const muted = isDark ? '#9ca3af' : '#6b7280';
  const inputBg = isDark ? '#171a1f' : '#f9fafb';
  const footerBg = isDark ? '#1c2129' : '#f9fafb';
  const rowHover = isDark ? '#262b35' : '#f3f4f6';
  const labelCls = 'text-[11px] uppercase tracking-wide mb-1 block';
  const inputCls = 'w-full text-xs rounded px-2.5 py-1.5 border focus:outline-none focus:ring-1 focus:ring-blue-500';

  const showToast = () => {
    setToastVisible(true);
    setToastFading(false);
    window.setTimeout(() => setToastFading(true), 800);
    window.setTimeout(() => setToastVisible(false), 1200);
  };

  const endpoints = useMemo(() => {
    if (!currentProject || !currentDesign || !selectedCable) return {
      from: null as PlacedEquipment | null,
      to: null as PlacedEquipment | null,
      fromPort: null as number | null,
      toPort: null as number | null,
    };

    const findPlaced = (id: string): PlacedEquipment | null => {
      const baseId = id.split(':')[0];
      const standalone = currentDesign.equipment.find(eq => eq.id === baseId || eq.equipment.id === baseId);
      if (standalone) return standalone;

      for (const rack of currentDesign.racks) {
        if (rack.id === baseId) {
          return {
            id: rack.id,
            equipment: { id: rack.id, name: rack.name, type: 'patch-panel', manufacturer: 'ubiquiti' },
            position: { x: rack.position.x + 20, y: rack.position.y + 20 },
          } as PlacedEquipment;
        }
        const inRack = rack.equipment.find(re => re.equipment.id === baseId);
        if (inRack) {
          return {
            id: `${rack.id}:${inRack.equipment.id}`,
            equipment: inRack.equipment,
            position: { x: rack.position.x + 20, y: rack.position.y + 20 },
          } as PlacedEquipment;
        }
      }
      return null;
    };

    const fromParsed = parsePortEndpointId(selectedCable.from);
    const toParsed = parsePortEndpointId(selectedCable.to);

    return {
      from: findPlaced(selectedCable.from),
      to: findPlaced(selectedCable.to),
      fromPort: fromParsed?.number ?? null,
      toPort: toParsed?.number ?? null,
      fromKind: fromParsed?.kind ?? null,
      toKind: toParsed?.kind ?? null,
    };
  }, [currentProject, currentDesign, selectedCable]);

  if (!isOpen || !selectedCable) return null;

  const noop = () => {};
  const liveCable = currentDesign?.cables?.find(c => c.id === selectedCable.id) || selectedCable;
  const isExisting = !!liveCable.isExisting;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
      <div
        className="rounded-lg shadow-2xl w-[1100px] max-w-[96vw] mx-4 max-h-[88vh] overflow-hidden flex flex-col"
        style={{ backgroundColor: drawerBg, border: `1px solid ${border}` }}
      >
        <div className="flex items-center justify-between px-5 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${border}` }}>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide" style={{ color: muted }}>Cable</p>
            <h2 className="text-sm font-semibold mt-0.5 truncate flex items-center gap-1.5" style={{ color: text }}>
              <CableIcon size={14} />
              Edit Cable Ports
            </h2>
          </div>
          <div className="flex items-center gap-2 ml-3 flex-shrink-0">
            <button
              onClick={() => useProjectStore.getState().toggleCableExistingStatus(selectedCable.id)}
              className="px-2 py-0.5 rounded text-[11px] font-medium"
              style={{
                backgroundColor: isExisting
                  ? (isDark ? 'rgba(22,163,74,0.25)' : '#dcfce7')
                  : (isDark ? '#2b3140' : '#e5e7eb'),
                color: isExisting
                  ? (isDark ? '#4ade80' : '#15803d')
                  : muted,
              }}
              title={isExisting ? 'Mark as new infrastructure' : 'Mark as existing infrastructure'}
            >
              {isExisting ? 'Existing' : 'New'}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded transition-colors"
              style={{ color: muted }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = rowHover)}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-5 py-3 flex-shrink-0" style={{ borderBottom: `1px solid ${border}` }}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={labelCls} style={{ color: muted }}>Cable Name</label>
              <input
                type="text"
                value={selectedCable.label || ''}
                onChange={(e) => updateCableRun(selectedCable.id, { label: e.target.value })}
                onBlur={(e) => {
                  const newLabel = e.target.value.trim();
                  if (newLabel === '') return;
                  const duplicateCable = currentDesign?.cables.find(c =>
                    c.id !== selectedCable.id &&
                    c.label?.toLowerCase().trim() === newLabel.toLowerCase().trim()
                  );
                  if (duplicateCable) {
                    alert(`A cable with the name "${newLabel}" already exists. Please choose a different name.`);
                    updateCableRun(selectedCable.id, { label: selectedCable.label || '' });
                    e.target.focus();
                  }
                }}
                className={inputCls}
                style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                placeholder={`${selectedCable.type} Cable`}
              />
            </div>
            <div>
              <label className={labelCls} style={{ color: muted }}>Type</label>
              <select
                value={selectedCable.type}
                onChange={(e) => {
                  const newType = e.target.value as 'ethernet' | 'fiber' | 'power';
                  updateCableRun(selectedCable.id, { type: newType, subtype: newType === 'ethernet' ? selectedCable.subtype : '' });
                }}
                className={inputCls}
                style={{ backgroundColor: inputBg, borderColor: border, color: text }}
              >
                <option value="ethernet">Ethernet</option>
                <option value="fiber">Fiber</option>
                <option value="power">Power</option>
              </select>
            </div>
            {selectedCable.type === 'ethernet' && (
              <div>
                <label className={labelCls} style={{ color: muted }}>Ethernet Type</label>
                <select
                  value={selectedCable.subtype || ''}
                  onChange={(e) => updateCableRun(selectedCable.id, { subtype: e.target.value })}
                  className={inputCls}
                  style={{ backgroundColor: inputBg, borderColor: border, color: text }}
                >
                  <option value="">Select Ethernet Type</option>
                  <option value="Cat5e CCA Indoor">Cat5e CCA Indoor</option>
                  <option value="Cat5e CCA Outdoor">Cat5e CCA Outdoor</option>
                  <option value="Cat5e Bare Copper Indoor">Cat5e Bare Copper Indoor</option>
                  <option value="Cat5e Bare Copper Outdoor">Cat5e Bare Copper Outdoor</option>
                  <option value="Cat6 CCA Indoor">Cat6 CCA Indoor</option>
                  <option value="Cat6 CCA Outdoor">Cat6 CCA Outdoor</option>
                  <option value="Cat6 Bare Copper Indoor">Cat6 Bare Copper Indoor</option>
                  <option value="Cat6 Bare Copper Outdoor">Cat6 Bare Copper Outdoor</option>
                  <option value="Cat6a Indoor">Cat6a Indoor</option>
                  <option value="Cat6a Outdoor">Cat6a Outdoor</option>
                  <option value="Cat7 Indoor">Cat7 Indoor</option>
                  <option value="Cat7 Outdoor">Cat7 Outdoor</option>
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="px-5 py-3 grid grid-cols-2 gap-3 flex-1 overflow-auto min-h-0">
          <div className="w-full min-w-0">
            {endpoints.from ? (
              <PortsView
                equipment={endpoints.from}
                isOpen={true}
                onClose={noop}
                onPortSelect={(portId) => {
                  if (!selectedCable) return;
                  updateCableRun(selectedCable.id, { from: portId } as any);
                  showToast();
                }}
                embedded
                selectMode
                titleOverride={endpoints.from.equipment.name}
                allowedCableId={selectedCable?.id}
                highlightPort={endpoints.fromPort}
                highlightKind={endpoints.fromKind}
              />
            ) : (
              <div className="text-xs p-3 rounded" style={{ color: muted, border: `1px solid ${border}` }}>
                Endpoint not found in project
              </div>
            )}
          </div>
          <div className="w-full min-w-0">
            {endpoints.to ? (
              <PortsView
                equipment={endpoints.to}
                isOpen={true}
                onClose={noop}
                onPortSelect={(portId) => {
                  if (!selectedCable) return;
                  updateCableRun(selectedCable.id, { to: portId } as any);
                  showToast();
                }}
                embedded
                selectMode
                titleOverride={endpoints.to.equipment.name}
                allowedCableId={selectedCable?.id}
                highlightPort={endpoints.toPort}
                highlightKind={endpoints.toKind}
              />
            ) : (
              <div className="text-xs p-3 rounded" style={{ color: muted, border: `1px solid ${border}` }}>
                Endpoint not found in project
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 flex-shrink-0" style={{ borderTop: `1px solid ${border}`, backgroundColor: footerBg }}>
          <button
            onClick={() => {
              if (selectedCable) {
                setConfirmDelete({
                  type: 'cable',
                  id: selectedCable.id,
                  label: selectedCable.label || selectedCable.type,
                });
              }
            }}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded border transition-colors"
            style={{
              borderColor: isDark ? '#7f1d1d' : '#fecaca',
              color: isDark ? '#fca5a5' : '#b91c1c',
              backgroundColor: isDark ? 'rgba(127,29,29,0.25)' : '#fef2f2',
            }}
          >
            <Trash2 size={12} /> Delete Cable
          </button>
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs rounded border transition-colors"
            style={{ borderColor: border, color: muted }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = rowHover)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
          >
            Close
          </button>
        </div>
      </div>

      {confirmDelete && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="rounded-lg shadow-2xl w-[360px] max-w-[92vw]" style={{ backgroundColor: drawerBg, border: `1px solid ${border}` }}>
            <div className="px-4 py-2.5 text-sm font-semibold" style={{ borderBottom: `1px solid ${border}`, color: text }}>
              Delete cable run?
            </div>
            <div className="p-4 text-xs" style={{ color: muted }}>
              Are you sure you want to delete <span className="font-medium" style={{ color: text }}>{confirmDelete.label}</span>? This action cannot be undone.
            </div>
            <div className="px-4 py-2.5 flex justify-end gap-2" style={{ borderTop: `1px solid ${border}`, backgroundColor: footerBg }}>
              <button
                className="px-3 py-1.5 text-xs rounded border"
                style={{ borderColor: border, color: muted }}
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700"
                onClick={() => {
                  deleteCableRun(confirmDelete.id);
                  setConfirmDelete(null);
                  onClose();
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {toastVisible && (
        <div className={`fixed left-1/2 -translate-x-1/2 bottom-4 bg-green-700 text-white text-[11px] px-3 py-1 rounded shadow transition-opacity duration-300 z-[90] ${toastFading ? 'opacity-0' : 'opacity-100'}`}>
          Port updated
        </div>
      )}
    </div>
  );
};

export default CablePortEditor;
